/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import PhoneNumberoReadUniqueQuery from './__query__/index.phoneNumbero.read.query'
import PhoneNumberoUpsertMutation from './__mutation__/index.phoneNumbero.upsert.mutation'


/*
 * OBJECTS
 */
const Index = ({ isOpen, isCreateOnly, onClose, passOn }) => {
  // Hook assignment.
  const [error, setError] = React.useState('')
  const [forceReRender, setForceReRender] = React.useState('')
  const [QueryPhoneNumberoReadUnique, QueryPhoneNumberoReadUniqueResponse] = useLazyQuery(PhoneNumberoReadUniqueQuery, { 'variables': { 'phoneNumberoId': passOn?.phoneNumberoId } })
  const [MutationPhoneNumberoUpsert, MutationPhoneNumberoUpsertResponse] = useMutation(PhoneNumberoUpsertMutation)
  const _formDataRef = React.useRef({})

  // Object assignment.
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'phoneNumbero': JoiBrowser.string().required()
    }).options({ 'allowUnknown': true })

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = _.pick(_formDataRef.current, _.identity)

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Execute account registration mutation.
    const _MutationPhoneNumberoUpsert = await MutationPhoneNumberoUpsert({
      'variables': {
        'phoneNumberoDirectoryId': isCreateOnly ? passOn?.phoneNumberoDirectoryId : void 0,
        'phoneNumbero': _formDataRef?.current?.phoneNumbero,
        'phoneNumberoId': isCreateOnly ? void 0 : passOn?.phoneNumberoId
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationPhoneNumberoUpsert instanceof Error) return _MutationPhoneNumberoUpsert

    // On Successful response from the mutation.
    if ('UPSERT_SUCCESSFUL' === _MutationPhoneNumberoUpsert?.data?.PhoneNumberoUpsert?.status) onClose?.()

    // Style Guide.
    return toast(_MutationPhoneNumberoUpsert?.data?.PhoneNumberoUpsert?.message)
  }

  // Event handler.
  React.useEffect(() => {
    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryPhoneNumberoReadUniqueQuery = await QueryPhoneNumberoReadUnique({ 'variables': { 'phoneNumberoId': isCreateOnly ? 'UN_KNOWN' : passOn?.phoneNumberoId } })

      // If query caught an exception then report failure.
      if (_QueryPhoneNumberoReadUniqueQuery instanceof Error) return _QueryPhoneNumberoReadUniqueQuery

      /*
       * If customer details fetch complete then
       * update its value.
       */
      if (_QueryPhoneNumberoReadUniqueQuery?.data?.PhoneNumberoReadUnique) {
        // Update form data.
        _formDataRef.current = {
          'phoneNumberoId': passOn?.phoneNumberoId,
          'phoneNumbero': _QueryPhoneNumberoReadUniqueQuery?.data?.PhoneNumberoReadUnique?.phoneNumbero
        }

        // Update state.
        return setForceReRender(String.random(8))
      }

      // Report failure.
      return void 0
    }; _Async()
  }, [passOn, isOpen])

  // Const assignment.
  const _isLoading = MutationPhoneNumberoUpsertResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryPhoneNumberoReadUniqueResponse?.loading

  // Return component.
  return (
    <form style={{ 'width': '100%' }} onSubmit={_SubmitForm} key={forceReRender}>
      <MemoizedInput
        disabled={_isInputDisabled}
        isRequired={true}
        name='phoneNumbero'
        label='PhoneNumbero'
        placeholder='e.g. "8826668515"'
        onChange={({ target }) => {
          // Over spreading.
          const { name, value } = target

          // Update form data.
          _formDataRef.current = {
            ..._formDataRef?.current,
            [name]: value
          }
        }}
        error={error}
        isInvalid={error?.includes('phoneNumbero')}
        data={_formDataRef?.current?.phoneNumbero}
      />
      <SubmitButton
        onSubmit={_SubmitForm}
        isLoading={_isLoading}
        disabled={_isInputDisabled}
        defaultText={isCreateOnly ? 'Create PhoneNumbero' : 'Update PhoneNumbero'} />
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
